import { SCHEMA } from '@vedalib/editor/lib/brand'
import React, { useMemo } from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import { FieldColorPicker, FieldSelect, FieldSlider, FormField } from 'components/formKit'
import Divider from 'components/uiKit/Divider'
import type { KeyIconsType } from 'components/uiKit/Icon'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import type { ISelectOption } from 'components/uiKit/Select/SelectTypes'
import { Typography } from 'components/uiKit/Typography'
import { BrandFieldShadow } from 'services/Branding/components'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useUpdateSchema } from 'services/Branding/hooks'
import { t } from 'services/Translation'

import * as s from './RectangleFormStyle.module.scss'
import type { RectangleFormBrandType } from './rectangleTypes'

const createBorderLabel = (iconName: KeyIconsType, text: string) => {
  const Label: React.FC = () => (
    <div className={s.label}>
      <Icon name={iconName} size={KitSize.S} /> {text}
    </div>
  )
  return Label
}

const BORDER_OPTIONS: ISelectOption<'none' | 'valuable' | 'solid' | 'dashed' | 'dotted'>[] = [
  {
    value: 'none',
    label: '',
    options: [{ label: t('input.option.withoutBorder'), value: 'none' }],
  },
  {
    value: 'valuable',
    label: <Divider free />,
    options: [
      { value: 'solid', label: createBorderLabel('solid', t('input.option.solid')) },
      { value: 'dashed', label: createBorderLabel('dash', t('input.option.dashed')) },
      { value: 'dotted', label: createBorderLabel('dots', t('input.option.dotted')) },
    ],
  },
]

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.rectangle.style, {
  rectangle: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
  },
  border: {
    borderStyle: genBrandField({
      as: FieldSelect<'none' | 'valuable' | 'solid' | 'dashed' | 'dotted'>,
      label: t('input.label.borderType'),
      props: { options: BORDER_OPTIONS, fluid: true },
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.rectangle.schema.boxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
})

const RectangleFormStyle: RectangleFormBrandType = ({ device, subConfigs }) => {
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottom gap sides>
        <FormField config={schema.rectangle.backgroundColor} />
        <FormField config={schema.rectangle.borderRadius} />
        <FormField config={schema.border.borderStyle} />
        <FormField config={schema.border.borderColor} />
        <FormField config={schema.border.borderWidth} />

        <Typography weight='medium'>{t('style.effects')}</Typography>
        <FormField config={schema.effects.boxShadow} />
      </Padding>
    </LayoutScroll>
  )
}

export default React.memo(RectangleFormStyle) as typeof RectangleFormStyle
